import { useState, useRef } from 'react';
import { useContent } from '@/hooks/useContent';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

export default function ContactPage() {
  const { t } = useContent('contact');
  const { t: imgT } = useContent('images');
  const { t: tc } = useContent('common');
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const [charCount, setCharCount] = useState(0);
  const formRef = useRef<HTMLFormElement>(null);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const honeypot = formData.get('website_alt') as string;
    if (honeypot && honeypot.trim() !== '') {
      setFormStatus('success');
      return;
    }
    formData.delete('website_alt');
    const params = new URLSearchParams();
    formData.forEach((value, key) => {
      params.append(key, value.toString());
    });
    setFormStatus('loading');
    setFormError('');
    try {
      const response = await fetch('https://readdy.ai/api/form/d9603urjjt5fme3c53d0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });
      const responseText = await response.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string } } = {};
      try { parsed = JSON.parse(responseText); } catch { parsed = {}; }
      const serverMsg = parsed?.meta?.message || parsed?.meta?.detail || responseText;
      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
        setCharCount(0);
      } else if (typeof serverMsg === 'string' && serverMsg.toLowerCase().includes('spam')) {
        setFormStatus('error');
        setFormError(tc('common_error_spam'));
      } else {
        setFormStatus('error');
        setFormError(serverMsg || tc('common_error'));
      }
    } catch {
      setFormStatus('error');
      setFormError(tc('common_error_connection'));
    }
  };

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <main>
        {/* Hero */}
        <section className="relative w-full h-[340px] md:h-[420px] flex items-center justify-center overflow-hidden pt-12 md:pt-16">
          <div className="absolute inset-0">
            <img
              alt="FMO LLC Insulation Services contact"
              className="w-full h-full object-cover object-center"
              src={imgT('contact_panel')}
            />
            <div className="absolute inset-0 bg-black/60"></div>
          </div>
          <div className="relative z-10 flex items-center justify-center">
            <div className="bg-black/60 border-2 border-primary-500 rounded-xl px-14 py-6 md:px-20 md:py-7">
              <h1 className="text-3xl md:text-4xl font-extrabold text-white uppercase tracking-[0.2em] text-center">{t('contact_hero_title')}</h1>
            </div>
          </div>
        </section>

        {/* Quick Info Bar */}
        <section className="w-full bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
              <div className="group flex items-center gap-5 px-6 py-8 border-b sm:border-b lg:border-b-0 lg:border-r border-primary-500/20 hover:bg-primary-500/10 transition-all duration-300 cursor-pointer">
                <div className="w-12 h-12 flex items-center justify-center bg-background-50 group-hover:bg-primary-500 rounded-full shrink-0 transition-all duration-300">
                  <i className="ri-phone-line text-primary-500 group-hover:text-white text-lg w-5 h-5 flex items-center justify-center transition-all duration-300"></i>
                </div>
                <div>
                  <p className="text-primary-500 text-[10px] uppercase tracking-[0.15em] font-bold mb-0.5">{t('quick_call')}</p>
                  <a href="tel:+15747219810" className="text-foreground-50 text-sm font-bold hover:text-primary-500 transition-colors block">(574) 721-9810</a>
                </div>
              </div>
              <div className="group flex items-center gap-5 px-6 py-8 border-b sm:border-b lg:border-b-0 lg:border-r border-primary-500/20 hover:bg-primary-500/10 transition-all duration-300 cursor-pointer">
                <div className="w-12 h-12 flex items-center justify-center bg-background-50 group-hover:bg-primary-500 rounded-full shrink-0 transition-all duration-300">
                  <i className="ri-mail-line text-primary-500 group-hover:text-white text-lg w-5 h-5 flex items-center justify-center transition-all duration-300"></i>
                </div>
                <div className="min-w-0">
                  <p className="text-primary-500 text-[10px] uppercase tracking-[0.15em] font-bold mb-0.5">{t('quick_email')}</p>
                  <a href="mailto:miguelceo@fmoinsulation.com" className="text-foreground-50 text-xs font-bold hover:text-primary-500 transition-colors break-all">miguelceo@fmoinsulation.com</a>
                </div>
              </div>
              <div className="group flex items-center gap-5 px-6 py-8 border-b sm:border-b lg:border-b-0 lg:border-r border-primary-500/20 hover:bg-primary-500/10 transition-all duration-300 cursor-pointer">
                <div className="w-12 h-12 flex items-center justify-center bg-background-50 group-hover:bg-primary-500 rounded-full shrink-0 transition-all duration-300">
                  <i className="ri-share-forward-line text-primary-500 group-hover:text-white text-lg w-5 h-5 flex items-center justify-center transition-all duration-300"></i>
                </div>
                <div>
                  <p className="text-primary-500 text-[10px] uppercase tracking-[0.15em] font-bold mb-0.5">{t('quick_follow')}</p>
                  <span className="text-foreground-50 text-sm font-bold block">FMO LLC</span>
                </div>
              </div>
              <div className="group flex items-center gap-5 px-6 py-8 hover:bg-primary-500/10 transition-all duration-300 cursor-pointer">
                <div className="w-12 h-12 flex items-center justify-center bg-background-50 group-hover:bg-primary-500 rounded-full shrink-0 transition-all duration-300">
                  <i className="ri-map-pin-line text-primary-500 group-hover:text-white text-lg w-5 h-5 flex items-center justify-center transition-all duration-300"></i>
                </div>
                <div>
                  <p className="text-primary-500 text-[10px] uppercase tracking-[0.15em] font-bold mb-0.5">{t('quick_area')}</p>
                  <span className="text-foreground-50 text-sm font-bold block">Lafayette, Indiana</span>
                  <p className="text-foreground-400 text-[10px] mt-0.5">{tc('common_local')}</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Form Section */}
        <section className="relative w-full overflow-hidden">
          <div className="absolute inset-0">
            <img
              alt="FMO LLC insulation contact background"
              className="w-full h-full object-cover object-center"
              src={imgT('contact_bg')}
            />
            <div className="absolute inset-0 bg-black/70"></div>
          </div>
          <div className="relative z-10 py-16 md:py-20">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <div className="flex flex-col lg:flex-row items-stretch gap-10 lg:gap-16">
                {/* Left Info Panel */}
                <div className="w-full lg:w-[40%] flex">
                  <div className="bg-black/60 border border-white/10 rounded-xl p-8 md:p-10 flex flex-col flex-1">
                    <div className="flex justify-center mb-4">
                      <img
                        alt="FMO LLC Insulation Services"
                        className="h-20 w-auto object-contain"
                        src="https://static.readdy.ai/image/3ce9d24c92e6c33dbaca65a0380531ab/059954b6faaad54bb7acfcf00b0835ac.png"
                      />
                    </div>
                    <h3 className="text-white font-bold text-base text-center mb-3">{t('panel_company')}</h3>
                    <p className="text-white/70 text-sm leading-relaxed text-center mb-8">
                      {t('panel_desc')}
                    </p>
                    <div className="bg-white/5 border border-white/10 rounded-lg p-5 mb-4">
                      <h4 className="text-primary-500 text-[10px] uppercase tracking-[0.15em] font-bold mb-4">{t('panel_area_title')}</h4>
                      <div className="flex items-start gap-3 mb-3">
                        <i className="ri-map-pin-line text-primary-500 w-4 h-4 flex items-center justify-center mt-0.5"></i>
                        <div className="flex flex-col">
                          <span className="text-white text-sm font-semibold">{t('panel_area_main')}</span>
                          <span className="text-white/60 text-xs">{t('panel_area_sub')}</span>
                        </div>
                      </div>
                    </div>
                    <div className="bg-white/5 border border-white/10 rounded-lg p-5 mb-4">
                      <h4 className="text-primary-500 text-[10px] uppercase tracking-[0.15em] font-bold mb-4">{t('panel_hours_title')}</h4>
                      <div className="space-y-2">
                        <div className="flex items-center gap-3">
                          <i className="ri-time-line text-primary-500 w-4 h-4 flex items-center justify-center"></i>
                          <div className="flex flex-col">
                            <span className="text-white text-sm">{t('panel_hours_weekdays')}</span>
                            <span className="text-white text-sm">{t('panel_hours_weekends')}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="bg-white/5 border border-white/10 rounded-lg p-5 mb-4">
                      <h4 className="text-primary-500 text-[10px] uppercase tracking-[0.15em] font-bold mb-4">{t('panel_why_title')}</h4>
                      <div className="space-y-3">
                        {[t('panel_why_1'), t('panel_why_2'), t('panel_why_3'), t('panel_why_4')].map((item) => (
                          <div key={item} className="flex items-start gap-3">
                            <i className="ri-check-line text-primary-500 w-4 h-4 flex items-center justify-center mt-0.5"></i>
                            <span className="text-white/70 text-sm">{item}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="mt-auto bg-white/5 border border-white/10 rounded-lg p-4 text-center">
                      <p className="text-white/50 text-xs leading-relaxed">{t('panel_cta')}</p>
                    </div>
                  </div>
                </div>

                {/* Right Form Panel */}
                <div className="w-full lg:w-[60%] flex">
                  <div className="bg-black/60 border border-white/10 rounded-2xl p-8 md:p-10 flex flex-col flex-1">
                    <div className="mb-6">
                      <span className="text-primary-500 text-[10px] uppercase tracking-[0.15em] font-bold">{t('form_badge')}</span>
                      <div className="w-10 h-[3px] bg-primary-500 mt-2"></div>
                      <h2 className="text-white font-bold text-2xl mt-2">{t('form_title')}</h2>
                    </div>

                    {formStatus === 'success' ? (
                      <div className="flex flex-col items-center justify-center py-12 gap-4">
                        <div className="w-16 h-16 flex items-center justify-center bg-primary-500/20 rounded-full">
                          <i className="ri-check-line text-primary-500 text-3xl w-8 h-8 flex items-center justify-center"></i>
                        </div>
                        <p className="text-white text-center font-bold text-xl">{tc('common_success')}</p>
                        <p className="text-white/60 text-center text-sm">{tc('common_success_24h')}</p>
                        <button
                          onClick={() => setFormStatus('idle')}
                          className="whitespace-nowrap bg-primary-500 hover:bg-primary-600 text-white font-bold py-2 px-6 rounded-lg text-sm cursor-pointer transition-all mt-2"
                        >
                          {tc('common_send_another')}
                        </button>
                      </div>
                    ) : (
                      <form data-readdy-form="true" id="contact-form" ref={formRef} className="flex flex-col gap-4" onSubmit={handleSubmit}>
                        <input type="text" name="website_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly className="honeypot-field" />
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-white text-sm font-semibold mb-1.5">{tc('common_first_name')} <span className="text-red-400">*</span></label>
                            <input
                              placeholder={t('form_placeholder_name')}
                              className="w-full bg-white/10 border border-white/20 text-white placeholder-white/50 text-sm rounded-lg px-4 py-3 outline-none focus:border-primary-500 transition-all"
                              required
                              name="firstName"
                            />
                          </div>
                          <div>
                            <label className="block text-white text-sm font-semibold mb-1.5">{tc('common_last_name')} <span className="text-red-400">*</span></label>
                            <input
                              placeholder={t('form_placeholder_last')}
                              className="w-full bg-white/10 border border-white/20 text-white placeholder-white/50 text-sm rounded-lg px-4 py-3 outline-none focus:border-primary-500 transition-all"
                              required
                              name="lastName"
                            />
                          </div>
                        </div>
                        <div>
                          <label className="block text-white text-sm font-semibold mb-1.5">{tc('common_email')} <span className="text-red-400">*</span></label>
                          <input
                            placeholder={t('form_placeholder_email')}
                            className="w-full bg-white/10 border border-white/20 text-white placeholder-white/50 text-sm rounded-lg px-4 py-3 outline-none focus:border-primary-500 transition-all"
                            required
                            type="email"
                            name="email"
                          />
                        </div>
                        <div>
                          <label className="block text-white text-sm font-semibold mb-1.5">{tc('common_phone')} <span className="text-red-400">*</span></label>
                          <input
                            placeholder={t('form_placeholder_phone')}
                            className="w-full bg-white/10 border border-white/20 text-white placeholder-white/50 text-sm rounded-lg px-4 py-3 outline-none focus:border-primary-500 transition-all"
                            required
                            type="tel"
                            name="phone"
                          />
                        </div>
                        <div>
                          <label className="block text-white text-sm font-semibold mb-1.5">{tc('common_address')}</label>
                          <input
                            placeholder={t('form_placeholder_address')}
                            className="w-full bg-white/10 border border-white/20 text-white placeholder-white/50 text-sm rounded-lg px-4 py-3 outline-none focus:border-primary-500 transition-all"
                            name="address"
                          />
                        </div>
                        <div>
                          <label className="block text-white text-sm font-semibold mb-1.5">{tc('common_service_needed')} <span className="text-red-400">*</span></label>
                          <select
                            name="service"
                            className="w-full bg-white/10 border border-white/20 text-white text-sm rounded-lg px-4 py-3 outline-none focus:border-primary-500 transition-all cursor-pointer"
                            required
                            defaultValue=""
                          >
                            <option value="" disabled>{tc('common_select_service')}</option>
                            <option value="Spray Foam Insulation">{tc('spray_foam')}</option>
                            <option value="Batt Insulation">{tc('batt_insulation')}</option>
                            <option value="Roll Insulation">{tc('roll_insulation')}</option>
                            <option value="Cellulose Insulation">{tc('cellulose_insulation')}</option>
                            <option value="Blown-In Insulation">{tc('blown_in_insulation')}</option>
                            <option value="Attic Insulation">{tc('attic_insulation')}</option>
                            <option value="Wall Insulation">{tc('wall_insulation')}</option>
                            <option value="Crawl Space Insulation">{tc('crawl_space_insulation')}</option>
                            <option value="Metal Building Insulation">{tc('metal_building_insulation')}</option>
                            <option value="General Consultation">{tc('general_consultation')}</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-white text-sm font-semibold mb-1.5">
                            {tc('common_message')}
                            <span className="text-primary-500 text-xs ml-1">({charCount}/500)</span>
                          </label>
                          <textarea
                            placeholder={t('form_placeholder_message')}
                            className="w-full bg-white/10 border border-white/20 text-white placeholder-white/50 text-sm rounded-lg px-4 py-3 outline-none focus:border-primary-500 transition-all resize-none"
                            rows={4}
                            maxLength={500}
                            name="comments"
                            onChange={(e) => setCharCount(e.target.value.length)}
                          ></textarea>
                        </div>
                        {formStatus === 'error' && (
                          <p className="text-red-400 text-xs">{formError}</p>
                        )}
                        <button
                          type="submit"
                          disabled={formStatus === 'loading'}
                          className="whitespace-nowrap w-full bg-primary-500 hover:bg-primary-600 disabled:opacity-60 text-white font-bold py-4 rounded-lg text-sm flex items-center justify-center gap-2 transition-all duration-200 cursor-pointer mt-1"
                        >
                          {formStatus === 'loading' ? (
                            <><i className="ri-loader-4-line animate-spin w-5 h-5 flex items-center justify-center"></i>{tc('common_sending')}</>
                          ) : (
                            <><i className="ri-send-plane-line text-base w-5 h-5 flex items-center justify-center"></i>{t('form_submit')}</>
                          )}
                        </button>
                      </form>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Map */}
        <section className="w-full py-8 md:py-12 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="w-full h-[400px] md:h-[500px] rounded-2xl overflow-hidden border border-white/10">
              <iframe
                src="https://www.google.com/maps?q=Lafayette+IN+47903&output=embed"
                className="w-full h-full border-0"
                title="FMO LLC Insulation Services - Service Area Lafayette, Indiana"
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              ></iframe>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}